<template>
    <div>
        <div :style="indentStyle" :class="['border rounded-lg p-3 bg-white text-sm', depth > 0 ? 'border-l-4 border-l-blue-300' : 'border-gray-200']">
            <div class="flex items-start justify-between gap-2">
                <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                        <span v-if="depth > 0" class="text-blue-400 text-xs">↳</span>
                        <span class="font-medium text-gray-800">{{ attivita.title }}</span>
                        <span :class="['text-xs rounded px-1.5 py-0.5', statusClass]">{{ statusLabel }}</span>
                        <span :class="['text-xs font-medium', priorityClass]">{{ attivita.priority }}</span>
                        <span v-if="attivita.activity_type === 'goods_receipt' && attivita.goods_receipt_status === 'waiting'" class="text-xs rounded px-1.5 py-0.5 bg-amber-100 text-amber-700">📦 In attesa merce</span>
                        <span v-if="attivita.activity_type === 'goods_receipt' && attivita.goods_receipt_status === 'received'" class="text-xs rounded px-1.5 py-0.5 bg-green-100 text-green-700">✅ Merce ricevuta</span>
                    </div>
                    <div v-if="attivita.description" class="text-gray-500 mt-0.5 truncate">{{ attivita.description }}</div>
                    <div class="flex gap-4 mt-1 text-xs text-gray-400 flex-wrap">
                        <span v-if="attivita.end_date">Fine: {{ formatDate(attivita.end_date) }}</span>
                        <span v-if="attivita.goods_estimated_arrival_at">Arr. stimato: {{ formatDate(attivita.goods_estimated_arrival_at) }}</span>
                        <span v-if="roleName" class="bg-indigo-50 text-indigo-600 rounded px-1.5 py-0.5">{{ roleName }}</span>
                        <span>Assegnatari: {{ ownerNames }}</span>
                        <span v-if="attivita.total_hours > 0">{{ attivita.total_hours }}h registrate</span>
                    </div>
                </div>
                <div class="flex items-center gap-1 flex-shrink-0">
                    <button @click="$emit('aggiungi-figlio', attivita)"
                        class="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Aggiungi attività figlia">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                    </button>
                    <button @click="$emit('salva', attivita)"
                        class="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Modifica">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                    </button>
                    <button @click="$emit('elimina', attivita)"
                        class="p-1.5 text-red-400 hover:bg-red-50 rounded transition-colors" title="Elimina">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    </button>
                </div>
            </div>
        </div>
        <template v-if="attivita.children && attivita.children.length">
            <ActivityNode
                v-for="child in attivita.children"
                :key="child.id"
                :attivita="child"
                :all-attivita="allAttivita"
                :ordine="ordine"
                :riga="riga"
                :depth="depth + 1"
                @salva="$emit('salva', $event)"
                @elimina="$emit('elimina', $event)"
                @aggiorna-parent="$emit('aggiorna-parent', $event)"
                @aggiungi-figlio="$emit('aggiungi-figlio', $event)"
            />
        </template>
    </div>
</template>

<script>
export default {
    name: 'ActivityNode',
    props: {
        attivita: { type: Object, required: true },
        allAttivita: { type: Array, required: true },
        ordine: { type: Object, required: true },
        riga: { type: Object, default: null },
        depth: { type: Number, default: 0 },
    },
    emits: ['salva', 'elimina', 'aggiorna-parent', 'aggiungi-figlio'],
    computed: {
        statusClass() {
            const map = {
                nuova: 'bg-gray-100 text-gray-600',
                assegnata: 'bg-blue-100 text-blue-700',
                schedulata: 'bg-purple-100 text-purple-700',
                in_lavorazione: 'bg-yellow-100 text-yellow-700',
                completata: 'bg-green-100 text-green-700',
            };
            return map[this.attivita.status] || 'bg-gray-100 text-gray-600';
        },
        priorityClass() {
            const map = {
                bassa: 'text-green-600',
                media: 'text-yellow-600',
                alta: 'text-orange-600',
                critica: 'text-red-600',
            };
            return map[this.attivita.priority] || '';
        },
        statusLabel() {
            const map = {
                nuova: 'Nuova',
                assegnata: 'Assegnata',
                schedulata: 'Schedulata',
                in_lavorazione: 'In lavorazione',
                completata: 'Completata',
            };
            return map[this.attivita.status] || this.attivita.status;
        },
        ownerNames() {
            if (!this.attivita.owners || this.attivita.owners.length === 0) {
                return this.attivita.role ? this.attivita.role.name : '-';
            }
            return this.attivita.owners.map(o => o.name).join(', ');
        },
        roleName() {
            return this.attivita.role ? this.attivita.role.name : null;
        },
        indentStyle() {
            return { marginLeft: (this.depth * 24) + 'px' };
        },
    },
    methods: {
        statusBadgeClass(status) {
            const map = {
                nuova: 'bg-gray-100 text-gray-700',
                assegnata: 'bg-blue-100 text-blue-700',
                schedulata: 'bg-purple-100 text-purple-700',
                in_lavorazione: 'bg-orange-100 text-orange-700',
                completata: 'bg-green-100 text-green-700',
            };
            return map[status] || 'bg-gray-100 text-gray-700';
        },
        priorityBadgeClass(priority) {
            const map = {
                bassa: 'bg-green-100 text-green-700',
                media: 'bg-yellow-100 text-yellow-700',
                alta: 'bg-orange-100 text-orange-700',
                critica: 'bg-red-100 text-red-700',
            };
            return map[priority] || 'bg-gray-100 text-gray-700';
        },
        formatDate(dateStr) {
            if (!dateStr) return '-';
            const d = new Date(dateStr);
            if (isNaN(d)) return dateStr;
            return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },
    },
};
</script>
